import { FC, useCallback, useEffect, useState } from "react"
import { Actions, ControlProps, JsonSchema } from "@jsonforms/core"
import { useJsonForms, withJsonFormsControlProps } from "@jsonforms/react"
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material"
import { isEqual } from "lodash"
import { useAppSelector } from "@/core/app/hooks"
import { RootState } from "@/core/app/store"
import InputSummary from "../NewForm/InputSummary"

const ArchElement: FC<{
  jsonSchema: JsonSchema
  inputKey: string
  subPropKeys: string[]
  data: any
  updateInput: (data: any) => void
}> = ({ jsonSchema, inputKey, subPropKeys, data, updateInput }) => {
  const [selectedChecks, setSelectedChecks] = useState(
    data ? { ...data[inputKey] } : {},
  )

  useEffect(() => {
    if (!isEqual( data ? { ...data[inputKey] } : {}, selectedChecks)) {
      // if data is updated from outside/programmatically, update the selectedChecks
      setSelectedChecks({ ...data[inputKey] })
    }
  }, [data])

  const [selected, setSelected] = useState<string>(
    data[inputKey]
      ? data[inputKey][subPropKeys[1]]
      : jsonSchema[subPropKeys[1]].default,
  )

  const handleChangeSelect = (event: SelectChangeEvent) => {
    setSelected(event.target.value as string)
    setSelectedChecks({
      ...selectedChecks,
      [event.target.name]: event.target.value,
    })
  }

  useEffect(() => {
    const obj = {}
    obj[inputKey] = {
      ...selectedChecks,
    }
    updateInput(obj)
  }, [selectedChecks, inputKey, updateInput])

  const handleChangeInput = (
    event: React.ChangeEvent<HTMLInputElement>,
    value: string,
  ) => {
    setSelectedChecks({
      ...selectedChecks,
      [event.target.name]: event.target.checked ? value : "",
    })
  }
  if (!selectedChecks[subPropKeys[0]]) return
  return (
    <>
      <Box
        sx={{
          flexDirection: "row",
          display: "flex",
          justifyContent: jsonSchema?.showAlignerType === true ? "space-around": "start",
          mb: selectedChecks[subPropKeys[0]] ? 0 : 2,
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={!!selectedChecks[subPropKeys[0]]}
              // onChange={(e) =>
              //   handleChangeInput(e, jsonSchema[subPropKeys[0]].label)
              // }
              sx={{
                "&.Mui-checked": {
                  color: "grey", // disabled and uncheckable by user
                },
              }}
              name={subPropKeys[0]}
            />
          }
          label={jsonSchema[subPropKeys[0]].label}
        />
        {jsonSchema?.showAlignerType !== false && (
          <FormControl sx={{ minWidth: 220 }}>
            <Select
              id={`${subPropKeys[1]}`}
              value={selected}
              name={`${subPropKeys[1]}`}
              onChange={handleChangeSelect}
            >
              {jsonSchema[subPropKeys[1]].enum?.map((e, i) => (
                <MenuItem key={`${e}+${i}`} value={e}>
                  {e}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>
      <Box
        sx={{
          flexDirection: "column",
          display: "flex", //selectedChecks[subPropKeys[0]] ? "flex" : "none",
          mx: 4,
          mt: 0,
          mb: 1,
        }}
      >
        {jsonSchema[subPropKeys[2]].items.oneOf.map((item) => (
          <FormControlLabel
            key={item.const}
            control={
              <Checkbox
                checked={!!selectedChecks[item.const]}
                onChange={(e) => handleChangeInput(e, item.title)}
                name={item.const}
              />
            }
            label={item.title}
          />
        ))}
      </Box>
    </>
  )
}

const UArchesTreat: FC<ControlProps> = (props) => {
  const { handleChange, path, data: idata, label, schema: ischema } = props
  const [isError, setIsError] = useState<boolean>(false)
  const [outJson, setOutJson] = useState<any>()
  const { core, dispatch } = useJsonForms()
  const [errors, setErrors] = useState("")
  const propsKeys = Object.keys(ischema.properties)
  const [data, setData] = useState<any>(idata || {})
  const [schema, setSchema] = useState<JsonSchema>(ischema || {})

  const { caseDetail } = useAppSelector((state: RootState) => state.caseService)

  const checkArchesforRetainer = (data) => {
    if (!data) return
    const values = Object.values(data)
    const checkedItems = values.filter((item) => item.isChecked)
    const allAreRetainers = checkedItems.every(
      (item) => item.aligner_type === "Retainer Only",
    )

    return (
      (checkedItems.length === 1 && allAreRetainers) ||
      (checkedItems.length === 2 && allAreRetainers)
    )
  }

  useEffect(() => {
    try {
      const udesign_json = JSON.parse(caseDetail?.udesign_json)

      if (
        udesign_json &&
        schema?.properties &&
        idata
      ) {

        let hasUpperArch = ["A", "R"].includes(udesign_json?.UpperType) || udesign_json?.upperUploaded
        let hasLowerArch = ["A", "R"].includes(udesign_json?.LowerType) || udesign_json?.lowerUploaded

        const tempData = {
          ...(idata || {}),
        }

        const tempSchema = {
          ...(schema || {}),
        }
        // Handle Upper Arch
        if (!hasUpperArch) {
          delete tempData.upper
          delete tempSchema.upper
        } else {
          tempData.upper = {
            ...(tempData.upper || {}),
            isChecked: "Upper",
            cover_lingual_bar: tempData.upper?.cover_lingual_bar ?? "",
            aligner_type: tempData.upper?.aligner_type ?? "Aligner",
          }
        }

        // Handle Lower Arch
        if (!hasLowerArch) {
          delete tempData.lower
          delete tempSchema.lower
        } else {
          tempData.lower = {
            ...(tempData.lower || {}),
            isChecked: "Lower",
            cover_lingual_bar: tempData.lower?.cover_lingual_bar ?? "",
            aligner_type: tempData.lower?.aligner_type ?? "Aligner",
          }
        }
        setSchema(tempSchema)
        setData(tempData)
      }
    } catch (error) {
      console.error("Error updating arches_to_treat schema/data:", error)
    }
  }, [ischema, idata])

  useEffect(() => {
    const hasData =
      data &&
      ((data.upper && data.upper.isChecked) ||
        (data.lower && data.lower.isChecked))
    setIsError(!hasData)
    if (core?.schema.required?.indexOf(path) === -1) return

    if (hasData) {
      if (checkArchesforRetainer(data)) {
        setIsError(true)
        updateErrors("At least one arch must have an Aligner.")
      } else {
        updateErrors("")
      }
    } else {
      updateErrors("This is a required field")
    }
  }, [data])

  const updateErrors = (message: string) => {
    setErrors(message)
    dispatch &&
      dispatch(
        Actions.updateErrors([
          {
            instancePath: "/" + path,
            message,
            schemaPath: "",
            keyword: "",
            params: {},
          },
        ]),
      )
  }

  useEffect(() => {
    let rs = { ...data }
    if (outJson) {
      // Merge upper arch data while preserving isChecked
      if (outJson.upper && rs.upper) {
        rs.upper = {
          ...rs.upper,
          ...outJson.upper,
          isChecked: rs.upper.isChecked
        }
      }
      // Merge lower arch data while preserving isChecked
      if (outJson.lower && rs.lower) {
        rs.lower = {
          ...rs.lower,
          ...outJson.lower,
          isChecked: rs.lower.isChecked
        }
      }
    }
    handleChange(path, rs)
  }, [handleChange, outJson, path])

  const subProps = (key: string) => {
    return schema.properties[key]?.properties
  }

  const updateInput = useCallback((outData: any) => {
    setOutJson(outData)
  }, [])

  const generateSummary = (data: any): string => {
    if (!data) return "No arches selected."

    const formatArch = (label: string, archData: any): string | null => {
      if (!archData?.isChecked) return null

      const header = `${label} : ${archData.aligner_type || ""}`.trim()
      const details = [
        archData.bracket_removal ? "Bracket Removal" : "",
        archData.cover_lingual_bar ? "Cover Lingual Bar" : "",
        archData.remove_lingual_bar ? "Remove Lingual Bar" : "",
      ]
        .filter(Boolean)
        .join(", ")

      return details ? `${header}\n${details}` : header
    }

    const summary = [
      formatArch("Upper", data.upper),
      formatArch("Lower", data.lower),
    ].filter(Boolean)

    return summary.length > 0 ? summary.join("\n\n") : "No arches selected."
  }

  return (
    <>
      {schema?.summary ? (
        <InputSummary label={label} inputVal={generateSummary(data)} />
      ) : (
        <FormControl
          error={isError && core?.validationMode === "ValidateAndShow"}
          component="fieldset"
          variant="standard"
          sx={{ my: 1 }}
        >
          <FormLabel
            sx={{
              my: 2,
              color: "rgba(0, 0, 0, 0.60)",
              "&.Mui-focused": {
                color: "rgba(0, 0, 0, 0.60)",
              },
            }}
          >
            {label}
          </FormLabel>
          {schema &&
            propsKeys.map((p, index) => (
              <Box key={index}>
                <ArchElement
                  jsonSchema={subProps(p)}
                  inputKey={p}
                  subPropKeys={schema.archKeys}
                  updateInput={updateInput}
                  data={data}
                />
              </Box>
            ))}

          {isError && core?.validationMode === "ValidateAndShow" && (
            <FormHelperText sx={{ marginLeft: 0 }}>{errors}</FormHelperText>
          )}
        </FormControl>
      )}
    </>
  )
}

export default withJsonFormsControlProps(UArchesTreat)
